<template>
<AppLayout>
  <div class="transaction">
    <section id="transaction-products">
      <div class="container">
        <h6 style="margin-bottom: 0px;" class="fw-bold text-primary">Purchase Requests</h6>
        
        <div class="warehouse-inventory-card-wrapper">
          <div class="warehouse-inventory-card">
            <p>Pending <br>Requests</p>
            <strong class="orange">{{ stats.pending || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Approved <br>Requests</p>
            <strong class="green">{{ stats.approved || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Processing <br>Requests</p>
            <strong class="blue">{{ stats.processing || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Completed <br>Requests</p>
            <strong>{{ stats.completed || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Rejected <br>Requests</p>
            <strong class="red">{{ stats.rejected || 0 }}</strong>
          </div>
        </div>

        <div class="filter-search" style="margin-top: 0px;padding-left: 0px;">
          <div class="filter-search-header">
            <p>Filter</p>
          </div>
          
          <div class="filter-search-content">
            <div class="form">
              <div class="row">
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="status">Status</label>
                    <select v-model="filters.status" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </div>
                </div>
                
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="department">Department</label>
                    <select v-model="filters.department" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Departments</option>
                      <option v-for="d in departments" :key="d.id" :value="d.name">{{ d.name }}</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-2">
                  <div class="form-group">
                    <label for="project">Project</label>
                    <select v-model="filters.project" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Projects</option>
                      <option v-for="p in projects" :key="p.id" :value="p.name">{{ p.name }}</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-2">
                  <div class="form-group">
                    <label for="category">Category</label>
                    <select v-model="filters.categoryId" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Categories</option>
                      <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-2">
                  <div class="form-group">
                    <label for="requester">Requester</label>
                    <input type="text" v-model="filters.requester" @input="loadPurchaseRequests" 
                           placeholder="Search by requester..." class="form-control">
                  </div>
                </div>
                
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="dateFrom">Date From</label>
                    <input type="date" v-model="filters.dateFrom" @change="loadPurchaseRequests" class="form-control">
                  </div>
                </div>
                
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="dateTo">Date To</label>
                    <input type="date" v-model="filters.dateTo" @change="loadPurchaseRequests" class="form-control">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="data-table-wrapper">
          <div v-if="loading" class="text-center p-4">
            <i class="fas fa-spinner fa-spin"></i> Loading...
          </div>
          
          <table v-else class="table table-hover">
            <thead>
              <tr>
                <th>Request No.</th>
                <th>Requester</th>
                <th>Department</th>
                <th>Project</th>
                <th>Category</th>
                <th>Items</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="purchaseRequests.length === 0">
                <td colspan="10" class="text-center p-4">No purchase requests found</td>
              </tr>
              <tr v-for="request in purchaseRequests" :key="request.id">
                <td>{{ request.purchaseRequestNo }}</td>
                <td>{{ request.requestedBy?.name }}</td>
                <td>{{ request.department?.name || request.department || '-' }}</td>
                <td>{{ request.project?.name || request.project || '-' }}</td>
                <td>{{ formatCategory(request) }}</td>
                <td>{{ request.items?.length || 0 }}</td>
                <td>
                  <button :class="getStatusClass(request.status)" style="border-radius: 15px;">
                    {{ formatStatusLabel(request.status) }}
                  </button>
                </td>
                <td>
                  <button :class="getPriorityClass(request.urgency)" style="border-radius: 15px;">
                    {{ formatStatusLabel(request.urgency) }}
                  </button>
                </td>
                <td>{{ formatDate(request.createdAt, true) }}</td>
                <td>
                  <div class="btn-group">
                    <router-link :to="`/transaction/purchase-request/view/${request.id}`" 
                                 class="btn btn-sm btn-info" title="View">
                      <i class="fas fa-eye"></i>
                    </router-link>
                    
                    <router-link v-if="request.status === 'APPROVED'" 
App                                 :to="`/transaction/purchase-request/view/${request.id}`" 
                                 class="btn btn-sm btn-warning" title="Process">
                      <i class="fas fa-cogs"></i>
                    </router-link>
                    
                    <button v-if="canApprove() && request.status === 'PENDING'" 
                            @click="approveRequest(request.id)"
                            class="btn btn-sm btn-success" title="Approve">
                      <i class="fas fa-check"></i>
                    </button>
                    
                    <button v-if="canReject() && request.status === 'PENDING'" 
                            @click="openRejectModal(request)"
                            class="btn btn-sm btn-danger" title="Reject">
                      <i class="fas fa-times"></i>
                    </button>
                    
                    <router-link
                      v-if="canEdit(request)"
                      :to="`/transaction/purchase-request/edit/${request.id}`"
                      class="btn btn-sm btn-primary"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-wrapper">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button @click="changePage(currentPage - 1)" class="page-link">Previous</button>
              </li>
              <li v-for="page in visiblePages" :key="page" 
                  class="page-item" :class="{ active: page === currentPage }">
                <button @click="changePage(page)" class="page-link">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button @click="changePage(currentPage + 1)" class="page-link">Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </section>

    <!-- Reject Modal -->
    <div v-if="showRejectModal" class="modal-backdrop">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Purchase Request</h5>
            <button @click="closeRejectModal" class="btn-close"></button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label for="rejectReason">Reason for Rejection</label>
              <textarea v-model="rejectForm.reason" 
                        id="rejectReason" 
                        class="form-control" 
                        rows="4" 
                        placeholder="Please provide a reason for rejecting this request..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeRejectModal" class="btn btn-secondary">Cancel</button>
            <button @click="confirmRejectRequest" class="btn btn-danger" :disabled="!rejectForm.reason.trim()">
              Reject Request
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'PurchaseRequestList',
  components: {
    AppLayout
  },
  data() {
    return {
      token: JSON.parse(localStorage.getItem('access_token')),
      purchaseRequests: [],
      stats: {},
      loading: false,
      filters: {
        status: '',
        categoryId: '',
        department: '',
        project: '',
        requester: '',
        dateFrom: '',
        dateTo: ''
      },
      categories: [],
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
      showRejectModal: false,
      rejectForm: {
        requestId: null,
        reason: ''
      }
    }
  },
  computed: {
    ...mapGetters(["getProjects", "getDepartments"]),

    projects() { return this.getProjects || [] },
    departments() { return this.getDepartments || [] },

    visiblePages() {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(this.totalPages, start + maxVisible - 1)
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.fetchDepartments()
    this.fetchProjects()
    this.loadCategories()
    this.loadStats()
    this.loadPurchaseRequests()
  },
  methods: {
    ...mapActions(["fetchProjects", "fetchDepartments"]),
    getErrorMessage(error, fallback) {
      // For fetch, error is usually just an Error with .message
      if (error && typeof error === 'object') {
        if (error.message) return error.message;
      }
      return fallback || 'An unknown error occurred';
    },

    formatCategory(request) {
      if (!request) return '-'

      // Helper to resolve by id from loaded categories
      const resolveById = (id) => {
        if (!id) return null
        const found = this.categories.find(c => Number(c.id) === Number(id))
        return found ? found.name : null
      }

      // Direct string fields (could be plain name or JSON serialized object)
      if (typeof request.category === 'string' && request.category) return request.category
      if (typeof request.tenderCategory === 'string' && request.tenderCategory) return request.tenderCategory

      // procurementCategory may be a JSON string (serialized object) or object
      let pcRaw = request.procurementCategory ?? request.procurement_category
      if (typeof pcRaw === 'string' && pcRaw.trim()) {
        // try to parse JSON string
        try {
          pcRaw = JSON.parse(pcRaw)
        } catch (e) {
          // not JSON, return the raw string
          return pcRaw
        }
      }

      // If procurementCategory is an object: check common shapes
      const pc = pcRaw
      if (pc && typeof pc === 'object') {
        if (pc.name) return pc.name
        // nested tenderCategory
        if (pc.tenderCategory && typeof pc.tenderCategory === 'object') {
          if (pc.tenderCategory.name) return pc.tenderCategory.name
          if (pc.tenderCategoryId) return resolveById(pc.tenderCategoryId)
        }
        // supplier tender category shape or legacy fields
        if (pc.tenderCategoryId) return resolveById(pc.tenderCategoryId)
        if (pc.tender_category_id) return resolveById(pc.tender_category_id)
        if (pc.categoryId) return resolveById(pc.categoryId)
        if (pc.supplierId && pc.tenderCategoryId) return resolveById(pc.tenderCategoryId)
      }

      // Fallbacks: request.category or tenderCategory objects
      if (request.category && typeof request.category === 'object' && request.category.name) return request.category.name
      if (request.tenderCategory && typeof request.tenderCategory === 'object' && request.tenderCategory.name) return request.tenderCategory.name

      return '-'
    },

    async loadStats() {
      try {
        const res = await fetch(process.env.VUE_APP_URL + '/purchase-requests/stats', {
          headers: {
            'Accept': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          }
        });
        if (!res.ok) {
          let msg = 'Failed to load stats';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        const data = await res.json();
        this.stats = data.data || data;
      } catch (error) {
        console.error('Error loading stats:', error);
        const msg = error?.message || 'Failed to load stats';
        this.$toast.error(msg);
      }
    },

    async loadPurchaseRequests() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          ...this.filters
        };
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });
        const query = new URLSearchParams(params).toString();
        const res = await fetch(process.env.VUE_APP_URL + `/purchase-requests?${query}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          }
        });

        console.log('Fetch response for purchase requests:', res);
        
        if (!res.ok) {
          let msg = 'Failed to load purchase requests';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        const data = await res.json();
        // Support both {status, data: {data, totalPages, ...}} and flat {data, totalPages, ...}
        const prData = data.data?.data ? data.data : data;
        this.purchaseRequests = prData.data || [];
        this.totalPages = prData.totalPages || 1;
        this.totalItems = prData.total || prData.totalItems || 0;
      } catch (error) {
        console.error('Error loading purchase requests:', error);
        const msg = error?.message || 'Failed to load purchase requests';
        this.$toast.error(msg);
      } finally {
        this.loading = false;
      }
    },

    async loadCategories() {
      try {
        const token = JSON.parse(localStorage.getItem('access_token'));
        const res = await fetch(process.env.VUE_APP_URL + '/tender-category', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
        });
        const result = await res.json();
        this.categories = Array.isArray(result.data) ? result.data : result;
      } catch (e) {
        console.error('Failed to load categories', e);
      }
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadPurchaseRequests()
      }
    },
    
    getStatusClass(status) {
      const s = (status || '').toString().toUpperCase()
      const classes = {
        PENDING: 'btn btn-sm btn-warning',
        ACTIVE: 'btn btn-sm btn-warning',
        PROCESSING: 'btn btn-sm btn-info',
        APPROVED: 'btn btn-sm btn-success',
        PROCESSED: 'btn btn-sm btn-success',
        COMPLETED: 'btn btn-sm btn-success',
        REJECTED: 'btn btn-sm btn-danger',
        CANCELLED: 'btn btn-sm btn-danger'
      }
      return classes[s] || 'btn btn-sm btn-warning'
    },
    
    getPriorityClass(priority) {
      const classes = {
        LOW: 'btn btn-sm btn-secondary',
        MEDIUM: 'btn btn-sm btn-primary',
        HIGH: 'btn btn-sm btn-warning',
        URGENT: 'btn btn-sm btn-danger blink'
      }
      return classes[priority] || 'btn btn-sm btn-secondary'
    },

    formatStatusLabel(value) {
      if (!value) return ''
      return value.toString().toLowerCase().split(/[_\s]+/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    },
    
    canEdit(request) {
      // Only allow editing if pending (case-insensitive)
      return (request && (request.status || '').toString().toUpperCase()) === 'PENDING';
    },
    
    canApprove() {
      // No permission check for now
      return true;
    },
    
    canReject() {
      // No permission check for now
      return true;
    },
    
    async approveRequest(requestId) {
      try {
        const res = await fetch(process.env.VUE_APP_URL + `/purchase-requests/${requestId}/approve`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          },
          body: JSON.stringify({})
        });
        if (!res.ok) {
          let msg = 'Failed to approve purchase request';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        this.$toast.success('Purchase request approved successfully');
        this.loadPurchaseRequests();
        this.loadStats();
      } catch (error) {
        console.error('Error approving request:', error);
        const msg = error?.message || 'Failed to approve purchase request';
        this.$toast.error(msg);
      }
    },
    
    openRejectModal(request) {
      this.rejectForm.requestId = request.id
      this.rejectForm.reason = ''
      this.showRejectModal = true
    },
    
    closeRejectModal() {
      this.showRejectModal = false
      this.rejectForm.requestId = null
      this.rejectForm.reason = ''
    },
    
    async confirmRejectRequest() {
      try {
        const res = await fetch(process.env.VUE_APP_URL + `/purchase-requests/${this.rejectForm.requestId}/reject`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          },
          body: JSON.stringify({ reason: this.rejectForm.reason })
        });
        if (!res.ok) {
          let msg = 'Failed to reject purchase request';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        this.$toast.success('Purchase request rejected');
        this.closeRejectModal();
        this.loadPurchaseRequests();
        this.loadStats();
      } catch (error) {
        console.error('Error rejecting request:', error);
        const msg = error?.message || 'Failed to reject purchase request';
        this.$toast.error(msg);
      }
    }
  }
}
</script>

<style scoped>
.warehouse-inventory-card-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.warehouse-inventory-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
}

.warehouse-inventory-card p {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 14px;
}

.warehouse-inventory-card strong {
  font-size: 24px;
  font-weight: bold;
}

.warehouse-inventory-card strong.red { color: #dc3545; }
.warehouse-inventory-card strong.green { color: #28a745; }
.warehouse-inventory-card strong.orange { color: #fd7e14; }
.warehouse-inventory-card strong.blue { color: #007bff; }

.data-table-wrapper {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.table {
  margin: 0;
}

.btn-group {
  display: flex;
  gap: 5px;
}

.btn-group .btn {
  padding: 4px 8px;
  font-size: 12px;
}

.badge {
  font-size: 11px;
  padding: 4px 8px;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.5; }
}

.blink {
  animation: blink 1s infinite;
}
</style>